import React, { useState } from 'react';
import { Search, MapPin, Calendar, CheckCircle, Clock, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrackingSection = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const whatsappNumber = '50688937777';
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setTrackingResult(null);

    try {
      const response = await axios.get(`${BACKEND_URL}/api/tracking/${trackingNumber.trim()}`);
      
      if (response.data.success) {
        setTrackingResult(response.data.data);
      } else {
        setError('No se encontró información para este número de tracking.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No se encontró el número de tracking. Verifica e intenta de nuevo.');
      } else {
        setError('Error al consultar el tracking. Por favor intenta más tarde.');
      }
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('entregado')) {
      return <CheckCircle size={20} />;
    } else if (statusLower.includes('tránsito') || statusLower.includes('transito')) {
      return <Clock size={20} />;
    } else {
      return <MapPin size={20} />;
    }
  };

  const getStatusClass = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('entregado')) {
      return 'status-badge entregado';
    } else if (statusLower.includes('tránsito') || statusLower.includes('transito')) {
      return 'status-badge en-transito';
    } else {
      return 'status-badge pendiente';
    }
  };

  return (
    <section id="tracking" className="py-20 px-6" style={{ background: 'var(--bg-section)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--sendhapy-teal)' }}>
            Rastrea tu paquete en tiempo real
          </h2>
          <p className="text-xl text-gray-600">
            Ingresa tu número de tracking y conoce el estado actual de tu paquete al instante
          </p>
        </div>

        {/* Tracking Form */}
        <form onSubmit={handleSearch} className="tracking-input-container mb-8">
          <div className="relative">
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Ingresa tu número de tracking (ej: SH123456)"
              className="tracking-input pr-14"
              data-testid="tracking-input"
              required
            />
            <button 
              type="submit"
              disabled={isLoading}
              data-testid="tracking-submit-btn"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors"
              style={{ 
                background: 'var(--sendhapy-yellow)', 
                color: 'var(--sendhapy-teal)' 
              }}
            >
              {isLoading ? (
                <div className="animate-spin">⏳</div>
              ) : (
                <Search size={20} />
              )}
            </button>
          </div>
        </form>

        {/* Example Tracking Numbers - Removido ya que ahora es real */}

        {/* Error Message */}
        {error && (
          <div data-testid="tracking-error" className="bg-red-50 border-2 border-red-300 rounded-2xl p-4 mb-6 text-center">
            <p className="text-red-700 font-medium">{error}</p>
            <p className="text-sm text-red-600 mt-2">
              ¿Necesitas ayuda? Contáctanos: <a href="mailto:hola@sendhapy.com" className="underline">hola@sendhapy.com</a>
            </p>
          </div>
        )}

        {/* Tracking Result */}
        {trackingResult && (
          <div data-testid="tracking-result" className="tracking-result">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
              <div>
                <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--sendhapy-teal)' }}>
                  Tracking: {trackingResult.trackingNumber}
                </h3>
                <div className={getStatusClass(trackingResult.status)}>
                  {getStatusIcon(trackingResult.status)}
                  {trackingResult.status}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                <MapPin size={24} style={{ color: 'var(--sendhapy-teal)', flexShrink: 0 }} />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Ubicación Actual</p>
                  <p className="font-semibold text-gray-900">{trackingResult.location}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                <Calendar size={24} style={{ color: 'var(--sendhapy-teal)', flexShrink: 0 }} />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Fecha Estimada</p>
                  <p className="font-semibold text-gray-900">{trackingResult.estimatedDelivery}</p>
                </div>
              </div>
            </div>

            {/* Información adicional */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              {trackingResult.contenido && (
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Contenido:</span> {trackingResult.contenido}
                </p>
              )}
              {trackingResult.peso_facturable_kg > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-semibold">Peso:</span> {trackingResult.peso_facturable_kg} kg facturables
                </p>
              )}
              {!trackingResult.contenido && !(trackingResult.peso_facturable_kg > 0) && (
                <p className="text-sm text-gray-500">Sin información adicional disponible</p>
              )}
            </div>

            {/* Timeline */}
            <div>
              <h4 className="font-semibold text-lg mb-4" style={{ color: 'var(--sendhapy-teal)' }}>
                Historial de Movimientos
              </h4>
              <div className="tracking-timeline">
                {trackingResult.history.map((item, index) => (
                  <div key={index} className="timeline-item">
                    <div className="flex justify-between items-start mb-1">
                      <p className="font-semibold text-gray-900">{item.status}</p>
                      <span className="text-sm text-gray-500">{item.time}</span>
                    </div>
                    <p className="text-sm text-gray-600">{item.location}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Contact CTA */}
        {!trackingResult && !error && (
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">¿No tienes un número de tracking aún?</p>
            <a 
              href="mailto:hola@sendhapy.com"
              className="btn-sendhapy-primary inline-flex"
            >
              Contáctanos
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrackingSection;
